<template>
    <div class="essay-question-page">
        <el-container class="layout">
            <!-- 左侧：作文类型卡片 -->
            <el-aside width="260px" class="type-aside">
                <div class="type-aside-head">
                    <div class="type-aside-title">
                        <i class="el-icon-tickets"></i>
                        <span>作文类型</span>
                    </div>
                    <el-button type="primary" size="mini" icon="el-icon-plus" @click="openTypeDialog(null)">新增</el-button>
                </div>
                <div class="type-list">
                    <div
                        class="type-card"
                        :class="{ active: selectedTypeId === '' }"
                        @click="selectType('')">
                        <span class="type-card-name">全部题目</span>
                        <span class="type-card-count">{{ totalCount }}</span>
                    </div>
                    <div
                        v-for="t in typeList"
                        :key="t.id"
                        class="type-card"
                        :class="{ active: selectedTypeId === t.id }"
                        draggable="true"
                        @click="selectType(t.id)"
                        @dragstart="onDragStart($event, t)"
                        @dragover.prevent="onDragOver($event, t)"
                        @drop="onDrop($event, t)">
                        <div class="type-card-top">
                            <i class="el-icon-rank drag-handle" title="拖动排序"></i>
                            <span class="type-card-name">{{ t.typeName }}</span>
                        </div>
                        <div class="type-card-bottom">
                            <span class="type-card-count">{{ t.count || 0 }} 题</span>
                            <div class="type-card-actions" @click.stop>
                                <i class="el-icon-edit" title="编辑" @click="openTypeDialog(t)"></i>
                                <i class="el-icon-delete" title="删除" @click="deleteType(t)"></i>
                            </div>
                        </div>
                        <div v-if="t.structureTips" class="type-card-tip">{{ t.structureTips }}</div>
                    </div>
                    <el-empty v-if="typeList.length === 0" description="暂无类型" :image-size="50"></el-empty>
                </div>
            </el-aside>

            <!-- 右侧：题目主区 -->
            <el-main class="main-area">
                <!-- 查询筛选卡片 -->
                <el-card shadow="always" class="filter-card">
                    <div slot="header" class="card-header">
                        <div class="card-header-left">
                            <i class="el-icon-edit-outline"></i>
                            <span>作文题目</span>
                            <el-divider direction="vertical"></el-divider>
                            <span class="header-sub">
                                当前：{{ selectedTypeId === '' ? '全部题目' : (typeMap[selectedTypeId] || '-') }}
                            </span>
                        </div>
                    </div>
                    <el-form :inline="true" :model="query" class="filter-form">
                        <el-form-item label="年级">
                            <el-select v-model="query.gradeId" placeholder="全部年级" clearable style="width:120px" @change="onGradeVolumeChange">
                                <el-option v-for="g in gradeList" :key="g.id" :label="g.gradeName" :value="g.id"/>
                            </el-select>
                        </el-form-item>
                        <el-form-item label="分册">
                            <el-select v-model="query.volume" placeholder="全部分册" clearable style="width:100px" @change="onVolumeChange">
                                <el-option v-for="item in filterVolumeOptions" :key="item.value" :label="item.label" :value="item.value"/>
                            </el-select>
                        </el-form-item>
                        <el-form-item label="单元">
                            <el-select v-model="query.unitId" placeholder="全部单元" clearable style="width:140px" :disabled="!query.gradeId || !query.volume" @change="handleSearch">
                                <el-option v-for="u in unitList" :key="u.id" :label="u.unitName" :value="u.id"/>
                            </el-select>
                        </el-form-item>
                        <el-form-item label="题目名称">
                            <el-input clearable v-model="query.title" placeholder="请输入题目名称" style="width:160px" @keyup.enter.native="handleSearch"/>
                        </el-form-item>
                        <el-form-item label="状态">
                            <el-select v-model="query.status" placeholder="全部状态" clearable style="width:100px" @change="handleSearch">
                                <el-option label="启用" :value="1"/>
                                <el-option label="禁用" :value="0"/>
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                        </el-form-item>
                    </el-form>
                </el-card>

                <!-- 表格卡片 -->
                <el-card shadow="always" class="table-card">
                    <div slot="header" class="card-header">
                        <div class="card-header-left">
                            <span>数据列表</span>
                            <span class="total-badge">共 <b>{{ total }}</b> 条</span>
                        </div>
                        <div class="card-header-right">
                            <el-button type="primary" icon="el-icon-plus" @click="btnInsert(null)">新增题目</el-button>
                        </div>
                    </div>
                    <el-table
                        :data="list"
                        border
                        stripe
                        v-loading="tableLoading"
                        element-loading-text="加载中..."
                        style="width: 100%">
                        <el-table-column type="index" label="序号" width="100" align="center"/>
                        <el-table-column label="年级" align="center" width="90">
                            <template slot-scope="scope">{{ gradeMap[scope.row.gradeId] || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="分册" align="center" width="80">
                            <template slot-scope="scope">{{ scope.row.volume || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="单元" width="130" show-overflow-tooltip>
                            <template slot-scope="scope">{{ scope.row.unitName || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="类型" width="100" align="center">
                            <template slot-scope="scope">{{ typeMap[scope.row.typeId] || '-' }}</template>
                        </el-table-column>
                        <el-table-column prop="title" label="题目名称" min-width="150" show-overflow-tooltip/>
                        <el-table-column prop="status" label="状态" align="center" width="80">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.status===1 ? 'success' : 'danger'" size="small" effect="plain">
                                    {{ scope.row.status===1 ? '启用' : '禁用' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="createTime" label="创建时间" width="170" align="center"/>
                        <el-table-column align="center" label="操作" width="100" fixed="right">
                            <template slot-scope="scope">
                                <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, scope.row)">
                                    <el-button size="mini">
                                        更多<i class="el-icon-arrow-down el-icon--right"></i>
                                    </el-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="edit" icon="el-icon-edit">编辑</el-dropdown-item>
                                        <el-dropdown-item command="analyze" icon="el-icon-data-analysis"
                                            :disabled="!!scope.row.fullTranslation">AI分析</el-dropdown-item>
                                        <el-dropdown-item command="detail" icon="el-icon-view">详情</el-dropdown-item>
                                        <el-dropdown-item command="delete" icon="el-icon-delete">删除</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                    <div class="pagination-wrapper" v-if="total > 0">
                        <el-pagination
                            background
                            layout="total, sizes, prev, pager, next, jumper"
                            @current-change="currentChange"
                            @size-change="sizeChange"
                            :page-sizes="[8, 10, 20, 50, 100]"
                            :current-page="query.currentPage"
                            :page-size="query.pageSize"
                            :total="total">
                        </el-pagination>
                    </div>
                </el-card>
            </el-main>
        </el-container>

        <!-- 题目新增/编辑对话框 -->
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="dialogVisible"
            width="600px"
            top="6vh"
            :show-close="false"
            @close="closeQuestionDialog"
            custom-class="essay-dialog">
            <div slot="title" class="dlg-header">
                <div class="dlg-header-left">
                    <div class="dlg-header-icon"><i class="el-icon-edit-outline"></i></div>
                    <div>
                        <div class="dlg-header-title">{{ dialogTitle }}</div>
                        <div class="dlg-header-desc">填写作文题目信息，系统将基于范文生成挖空训练</div>
                    </div>
                </div>
                <i class="el-icon-close dlg-header-close" @click="dialogVisible = false"></i>
            </div>
            <el-form :model="formData" ref="formRef" :rules="rules" label-width="80px">
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="年级" prop="gradeId">
                            <el-select v-model="formData.gradeId" placeholder="请选择" style="width:100%" @change="onFormGradeVolumeChange">
                                <el-option v-for="g in gradeList" :key="g.id" :label="g.gradeName" :value="g.id"/>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                            <el-form-item label="分册" prop="volume">
                                <el-select v-model="formData.volume" placeholder="请选择" style="width:100%" @change="onFormGradeVolumeChange">
                                    <el-option v-for="item in formVolumeOptions" :key="item.value" :label="item.label" :value="item.value"/>
                                </el-select>
                            </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="单元" prop="unitId">
                            <el-select v-model="formData.unitId" placeholder="请选择" style="width:100%" :disabled="!formData.gradeId || !formData.volume">
                                <el-option v-for="u in unitList" :key="u.id" :label="u.unitName" :value="u.id"/>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="类型" prop="typeId">
                            <el-select v-model="formData.typeId" placeholder="请选择" style="width:100%">
                                <el-option v-for="t in typeList" :key="t.id" :label="t.typeName" :value="t.id"/>
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="难度">
                    <el-rate v-model="formData.difficulty" :max="5" show-score style="margin-top:8px" score-template="{value}星"/>
                </el-form-item>
                <el-form-item label="题目名称" prop="title">
                    <el-input v-model="formData.title" maxlength="100" placeholder="例如：My Best Friend"/>
                </el-form-item>
                <el-form-item label="中文题干" prop="chinesePrompt">
                    <el-input type="textarea" :rows="4" v-model="formData.chinesePrompt" maxlength="2000"
                        placeholder="用中文描述写作要求，如：假设你是李华，你的笔友Tom来信询问你的学校生活，请写一封回信。"/>
                </el-form-item>
                <el-form-item label="参考范文" prop="modelEssay">
                    <el-input type="textarea" :rows="6" v-model="formData.modelEssay" maxlength="5000"
                        placeholder="输入英文范文，系统将自动基于范文生成挖空训练"/>
                </el-form-item>
                <el-form-item label="状态" prop="status">
                    <el-radio-group v-model="formData.status">
                        <el-radio :label="1">启用</el-radio>
                        <el-radio :label="0">禁用</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="submitLoading">确定</el-button>
            </div>
        </el-dialog>

        <!-- 作文类型新增/编辑对话框 -->
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="typeDialogVisible"
            width="560px"
            top="8vh"
            :show-close="false"
            @close="closeTypeDialog"
            custom-class="essay-dialog">
            <div slot="title" class="dlg-header">
                <div class="dlg-header-left">
                    <div class="dlg-header-icon"><i class="el-icon-tickets"></i></div>
                    <div>
                        <div class="dlg-header-title">{{ typeDialogTitle }}</div>
                        <div class="dlg-header-desc">维护作文体裁，供左侧选题与出题使用</div>
                    </div>
                </div>
                <i class="el-icon-close dlg-header-close" @click="typeDialogVisible = false"></i>
            </div>
            <el-form :model="typeForm" ref="typeFormRef" :rules="typeRules" label-width="80px">
                <el-form-item label="类型标识" prop="typeKey">
                    <el-input v-model="typeForm.typeKey" maxlength="30" placeholder="英文标识，如：speech、letter"></el-input>
                </el-form-item>
                <el-form-item label="类型名称" prop="typeName">
                    <el-input v-model="typeForm.typeName" maxlength="20" placeholder="例如：发言稿"></el-input>
                </el-form-item>
                <el-form-item label="段落结构" prop="structureTips">
                    <el-input type="textarea" v-model="typeForm.structureTips" maxlength="500" placeholder="例如：开头问候→中间内容→结尾祝愿" :rows="3"></el-input>
                </el-form-item>
                <el-form-item label="说明" prop="description">
                    <el-input type="textarea" v-model="typeForm.description" maxlength="200" placeholder="补充说明（可选）" :rows="2"></el-input>
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="排序" prop="sort">
                            <el-input-number v-model="typeForm.sort" :min="0" :max="9999" controls-position="right" style="width: 100%"></el-input-number>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="状态" prop="status">
                            <el-radio-group v-model="typeForm.status">
                                <el-radio :label="1">启用</el-radio>
                                <el-radio :label="0">禁用</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <div slot="footer">
                <el-button @click="typeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitTypeForm" :loading="typeSubmitLoading">确定</el-button>
            </div>
        </el-dialog>

        <!-- 题目详情弹窗 -->
        <el-dialog title="题目详情" :visible.sync="detailVisible" width="70%" top="4vh" class="question-dialog">
            <div v-if="detailData" class="detail-scroll">
                <!-- 基本信息卡片 -->
                <el-card shadow="never" class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-info"></i> 基本信息
                    </div>
                    <el-row :gutter="20" class="info-grid">
                        <el-col :span="6" class="info-cell">
                            <div class="info-label">年级</div>
                            <div class="info-value">{{ gradeMap[detailData.gradeId] || '-' }}</div>
                        </el-col>
                        <el-col :span="6" class="info-cell">
                            <div class="info-label">分册</div>
                            <div class="info-value">{{ detailData.volume || '-' }}</div>
                        </el-col>
                        <el-col :span="6" class="info-cell">
                            <div class="info-label">单元</div>
                            <div class="info-value">{{ detailData.unitName || '-' }}</div>
                        </el-col>
                        <el-col :span="6" class="info-cell">
                            <div class="info-label">类型</div>
                            <div class="info-value">{{ typeMap[detailData.typeId] || '-' }}</div>
                        </el-col>
                        <el-col :span="6" class="info-cell">
                            <div class="info-label">难度</div>
                            <div class="info-value">
                                <el-rate v-model="detailData.difficulty" :max="5" disabled show-score score-template="{value}星" style="display:inline-block"/>
                            </div>
                        </el-col>
                        <el-col :span="12" class="info-cell">
                            <div class="info-label">题目名称</div>
                            <div class="info-value">{{ detailData.title }}</div>
                        </el-col>
                        <el-col :span="12" class="info-cell">
                            <div class="info-label">状态</div>
                            <div class="info-value">
                                <el-tag :type="detailData.status===1?'success':'danger'" effect="dark" size="small">{{ detailData.status===1?'启用':'禁用' }}</el-tag>
                            </div>
                        </el-col>
                    </el-row>
                </el-card>

                <!-- 中文题干 -->
                <el-card shadow="never" class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-edit-outline"></i> 中文题干
                    </div>
                    <div class="text-block">{{ detailData.chinesePrompt }}</div>
                </el-card>

                <!-- 中英对照 -->
                <el-card shadow="never" class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-reading"></i> 中英对照
                    </div>
                    <el-row :gutter="16" class="bilingual-row">
                        <el-col :span="12">
                            <div class="bilingual-header">
                                <span class="lang-tag en">EN</span>
                                英文原文
                            </div>
                            <div class="bilingual-content en">{{ detailData.modelEssay }}</div>
                        </el-col>
                        <el-col :span="12">
                            <div class="bilingual-header">
                                <span class="lang-tag zh">中</span>
                                中文翻译
                            </div>
                            <div v-if="detailData.fullTranslation" class="bilingual-content zh">{{ detailData.fullTranslation }}</div>
                            <el-empty v-else description="暂无翻译" :image-size="40"></el-empty>
                        </el-col>
                    </el-row>
                </el-card>

                <!-- 短文挖空等级 -->
                <el-card shadow="never" class="detail-section" v-if="detailData.gapFillLow || detailData.gapFillMedium || detailData.gapFillHigh">
                    <div class="section-title">
                        <i class="el-icon-document-copy"></i> 短文挖空等级
                    </div>
                    <el-tabs v-model="activeGapFillTab" type="border-card">
                        <el-tab-pane label="初级" name="low">
                            <template v-if="parseJson(detailData.gapFillLow).length > 0">
                                <div v-for="item in parseJson(detailData.gapFillLow)" :key="item.sentenceNo" style="margin-bottom:14px">
                                    <div style="display:flex;align-items:baseline;gap:8px">
                                        <span style="color:#909399;font-size:12px;min-width:30px">{{ item.sentenceNo }}.</span>
                                        <span style="white-space:pre-wrap;line-height:1.8">{{ item.gappedSentence }}</span>
                                    </div>
                                    <div style="margin-left:38px;margin-top:4px">
                                        <el-tag v-for="(ans,i) in item.answers" :key="i" size="small" type="success" style="margin-right:6px;margin-bottom:4px">{{ ans }}</el-tag>
                                    </div>
                                </div>
                            </template>
                            <el-empty v-else description="暂无数据" :image-size="50"/>
                        </el-tab-pane>
                        <el-tab-pane label="中级" name="medium">
                            <template v-if="parseJson(detailData.gapFillMedium).length > 0">
                                <div v-for="item in parseJson(detailData.gapFillMedium)" :key="item.sentenceNo" style="margin-bottom:14px">
                                    <div style="display:flex;align-items:baseline;gap:8px">
                                        <span style="color:#909399;font-size:12px;min-width:30px">{{ item.sentenceNo }}.</span>
                                        <span style="white-space:pre-wrap;line-height:1.8">{{ item.gappedSentence }}</span>
                                    </div>
                                    <div style="margin-left:38px;margin-top:4px">
                                        <el-tag v-for="(ans,i) in item.answers" :key="i" size="small" type="warning" style="margin-right:6px;margin-bottom:4px">{{ ans }}</el-tag>
                                    </div>
                                </div>
                            </template>
                            <el-empty v-else description="暂无数据" :image-size="50"/>
                        </el-tab-pane>
                        <el-tab-pane label="高级" name="high">
                            <template v-if="parseJson(detailData.gapFillHigh).length > 0">
                                <div v-for="item in parseJson(detailData.gapFillHigh)" :key="item.sentenceNo" style="margin-bottom:14px">
                                    <div style="display:flex;align-items:baseline;gap:8px">
                                        <span style="color:#909399;font-size:12px;min-width:30px">{{ item.sentenceNo }}.</span>
                                        <span style="white-space:pre-wrap;line-height:1.8">{{ item.gappedSentence }}</span>
                                    </div>
                                    <div style="margin-left:38px;margin-top:4px">
                                        <el-tag v-for="(ans,i) in item.answers" :key="i" size="small" type="danger" style="margin-right:6px;margin-bottom:4px">{{ ans }}</el-tag>
                                    </div>
                                </div>
                            </template>
                            <el-empty v-else description="暂无数据" :image-size="50"/>
                        </el-tab-pane>
                    </el-tabs>
                </el-card>

                <!-- AI分析结果 -->
                <template v-if="detailData.fullTranslation">
                    <el-card shadow="never" class="detail-section">
                        <div class="section-title">
                            <i class="el-icon-data-analysis"></i> AI分析结果
                        </div>
                        <el-tabs v-model="activeAiTab" type="border-card">
                            <el-tab-pane label="固定搭配" name="collocations">
                                <el-table :data="parseJson(detailData.fixedCollocations)" size="small" border stripe style="width:100%">
                                    <el-table-column type="index" label="#" width="40"/>
                                    <el-table-column prop="english" label="英文" min-width="200"/>
                                    <el-table-column prop="chinese" label="中文" min-width="200"/>
                                </el-table>
                                <el-empty v-if="parseJson(detailData.fixedCollocations).length === 0" description="暂无数据" :image-size="50"/>
                            </el-tab-pane>
                            <el-tab-pane label="好词" name="words">
                                <el-table :data="parseJson(detailData.goodWords)" size="small" border stripe style="width:100%">
                                    <el-table-column type="index" label="#" width="40"/>
                                    <el-table-column prop="english" label="英文" min-width="160"/>
                                    <el-table-column prop="chinese" label="中文" min-width="160"/>
                                    <el-table-column prop="partOfSpeech" label="词性" width="80"/>
                                </el-table>
                                <el-empty v-if="parseJson(detailData.goodWords).length === 0" description="暂无数据" :image-size="50"/>
                            </el-tab-pane>
                            <el-tab-pane label="好句" name="sentences">
                                <el-table :data="parseJson(detailData.goodSentences)" size="small" border stripe style="width:100%">
                                    <el-table-column type="index" label="#" width="40"/>
                                    <el-table-column prop="english" label="英文" min-width="300"/>
                                    <el-table-column prop="chinese" label="中文" min-width="300"/>
                                </el-table>
                                <el-empty v-if="parseJson(detailData.goodSentences).length === 0" description="暂无数据" :image-size="50"/>
                            </el-tab-pane>
                            <el-tab-pane v-if="parseJson(detailData.sentenceTranslations).length > 0" label="逐句翻译" name="translations">
                                <el-table :data="parseJson(detailData.sentenceTranslations)" size="small" border stripe style="width:100%">
                                    <el-table-column type="index" label="#" width="40"/>
                                    <el-table-column prop="english" label="英文原句" min-width="300"/>
                                    <el-table-column prop="chinese" label="中文翻译" min-width="300"/>
                                </el-table>
                            </el-tab-pane>
                        </el-tabs>
                    </el-card>
                </template>
                <el-empty v-else description="尚未执行AI分析" style="padding:30px 0"/>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {
    reqQuestionPage, reqQuestionDetail, reqQuestionAdd, reqQuestionUpdate, reqQuestionDelete, reqQuestionAnalyze,
    reqTypeList, reqTypeAdd, reqTypeUpdate, reqTypeDelete, reqTypeSort
} from '@/api/education/essay'
import { reqGradeList } from '@/api/education/grade'
import { reqUnitsByGradeAndVolume, reqVolumeOptions } from '@/api/education/unit'

export default {
    name: 'EssayQuestion',
    data() {
        return {
            list: [],
            total: 0,
            totalCount: 0,
            typeList: [],
            typeMap: {},
            gradeList: [],
            gradeMap: {},
            unitList: [],
            unitMap: {},
            filterVolumeOptions: [],
            formVolumeOptions: [],
            selectedTypeId: '',
            dragFromId: null,
            tableLoading: false,
            query: {
                currentPage: 1, pageSize: 8,
                title: '', typeId: null, gradeId: null, volume: null, unitId: null, status: 1
            },
            // 题目弹窗
            dialogVisible: false,
            dialogTitle: '',
            formData: this.initForm(),
            rules: {
                title: [{ required: true, message: '请填写题目名称', trigger: 'blur' }],
                typeId: [{ required: true, message: '请选择类型', trigger: 'change' }],
                gradeId: [{ required: true, message: '请选择年级', trigger: 'change' }],
                chinesePrompt: [{ required: true, message: '请填写中文题干', trigger: 'blur' }],
                modelEssay: [{ required: true, message: '请填写参考范文', trigger: 'blur' }]
            },
            // 类型弹窗
            typeDialogVisible: false,
            typeDialogTitle: '',
            typeSubmitLoading: false,
            submitLoading: false,
            typeForm: this.initTypeForm(),
            typeRules: {
                typeKey: [
                    { required: true, message: '请输入类型标识', trigger: 'blur' },
                    { min: 2, max: 30, message: '长度在 2 到 30 个字符', trigger: 'blur' }
                ],
                typeName: [
                    { required: true, message: '请输入类型名称', trigger: 'blur' },
                    { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
                ]
            },
            detailVisible: false,
            detailData: null,
            activeAiTab: 'collocations',
            activeGapFillTab: 'low'
        }
    },
    created() {
        this.loadTypes()
        this.loadGrades()
        this.load()
    },
    methods: {
        initForm() {
            return { id: null, title: '', gradeId: null, volume: null, unitId: null, typeId: null, chinesePrompt: '', modelEssay: '', difficulty: 3, status: 1 }
        },
        initTypeForm() {
            return { id: null, typeKey: '', typeName: '', structureTips: '', description: '', sort: 0, status: 1 }
        },
        async loadTypes() {
            try {
                const r = await reqTypeList()
                this.typeList = r || []
                this.typeMap = {}
                this.typeList.forEach(t => { this.typeMap[t.id] = t.typeName })
            } catch (e) { console.error('加载类型失败:', e) }
        },
        async loadGrades() {
            try {
                const r = await reqGradeList()
                this.gradeList = r || []
                this.gradeMap = {}
                this.gradeList.forEach(g => { this.gradeMap[g.id] = g.gradeName })
            } catch (e) { console.error('加载年级失败:', e) }
        },
        async loadUnits(gradeId, volume) {
            if (!gradeId || !volume) { this.unitList = []; this.unitMap = {}; return }
            try {
                const r = await reqUnitsByGradeAndVolume(gradeId, volume)
                this.unitList = r || []
                this.unitMap = {}
                this.unitList.forEach(u => { this.unitMap[u.id] = u.unitName })
            } catch (e) { console.error('加载单元失败:', e) }
        },
        onGradeVolumeChange() {
            if (this.query.gradeId) {
                this.loadFilterVolumeOptions(this.query.gradeId)
            }
            this.query.unitId = null
            this.loadUnits(this.query.gradeId, this.query.volume)
            this.handleSearch()
        },
        onVolumeChange() {
            this.query.unitId = null
            this.loadUnits(this.query.gradeId, this.query.volume)
            this.handleSearch()
        },
        onFormGradeVolumeChange() {
            if (this.formData.gradeId) {
                this.loadFormVolumeOptions(this.formData.gradeId)
            }
            this.formData.unitId = null
            this.loadUnits(this.formData.gradeId, this.formData.volume)
        },
        async loadFilterVolumeOptions(gradeId) {
            try {
                const res = await reqVolumeOptions(gradeId)
                const list = (res && Array.isArray(res)) ? res : []
                this.filterVolumeOptions = list.map(v => ({ label: v, value: v }))
            } catch (e) { this.filterVolumeOptions = [] }
        },
        async loadFormVolumeOptions(gradeId) {
            try {
                const res = await reqVolumeOptions(gradeId)
                const list = (res && Array.isArray(res)) ? res : []
                this.formVolumeOptions = list.map(v => ({ label: v, value: v }))
            } catch (e) { this.formVolumeOptions = [] }
        },
        selectType(id) {
            this.selectedTypeId = id
            this.query.typeId = id || null
            this.query.currentPage = 1
            this.load()
        },
        async load() {
            this.tableLoading = true
            try {
                const r = await reqQuestionPage(this.query)
                this.list = r.records || []
                this.total = r.total || 0
                if (!this.query.typeId) {
                    this.totalCount = r.total || 0
                }
            } catch (e) {
                this.$message.error('加载题目列表失败')
                console.error(e)
            } finally {
                this.tableLoading = false
            }
        },
        handleSearch() {
            this.query.currentPage = 1
            this.load()
        },
        handleReset() {
            this.query = {
                currentPage: 1, pageSize: 8,
                title: '', typeId: this.selectedTypeId || null, gradeId: null, volume: null, unitId: null, status: null
            }
            this.unitList = []
            this.load()
        },
        currentChange(num) {
            this.query.currentPage = num
            this.load()
        },
        sizeChange(size) {
            this.query.pageSize = size
            this.query.currentPage = 1
            this.load()
        },
        btnInsert(row) {
            if (row) {
                this.dialogTitle = '编辑题目'
                this.formData = { ...this.initForm(), ...row }
                this.loadFormVolumeOptions(this.formData.gradeId)
                this.loadUnits(this.formData.gradeId, this.formData.volume)
            } else {
                this.dialogTitle = '新增题目'
                this.formData = this.initForm()
                // 若左侧已选中具体类型，自动带入
                if (this.selectedTypeId) {
                    this.formData.typeId = this.selectedTypeId
                }
            }
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate()
                }
            })
            this.dialogVisible = true
        },
        handleCommand(command, row) {
            switch (command) {
                case 'edit': this.btnInsert(row); break
                case 'analyze': this.btnAnalyze(row); break
                case 'detail': this.btnDetail(row); break
                case 'delete': this.btnDelete(row.id); break
            }
        },
        async btnDelete(id) {
            try {
                await this.$confirm('确定要删除该题目吗？删除后不可恢复。', '删除确认', {
                    confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
                })
                await reqQuestionDelete(id)
                this.$message.success('删除成功')
                this.load()
            } catch (e) {}
        },
        async btnAnalyze(row) {
            try {
                await this.$confirm(`确定对"${row.title}"执行AI分析？分析后将更新搭配/好词/好句/翻译。`, 'AI分析确认', {
                    confirmButtonText: '确定', cancelButtonText: '取消', type: 'info'
                })
                await reqQuestionAnalyze(row.id)
                this.$message.success('AI分析已触发，请稍后刷新查看结果')
                this.load()
            } catch (e) {}
        },
        async btnDetail(row) {
            try {
                const r = await reqQuestionDetail(row.id)
                this.detailData = r
                this.activeAiTab = 'collocations'
                this.activeGapFillTab = 'low'
                this.detailVisible = true
            } catch (e) {
                this.$message.error('加载题目详情失败')
                console.error(e)
            }
        },
        parseJson(str) {
            if (!str) return []
            if (Array.isArray(str)) return str
            try { return JSON.parse(str) } catch { return [] }
        },
        closeQuestionDialog() {
            this.$refs.formRef && this.$refs.formRef.clearValidate()
        },
        submitForm() {
            this.$refs.formRef.validate(async valid => {
                if (!valid) return
                this.submitLoading = true
                try {
                    const api = this.formData.id ? reqQuestionUpdate : reqQuestionAdd
                    await api(this.formData)
                    this.$message.success('操作成功')
                    this.dialogVisible = false
                    await this.loadTypes()
                    this.load()
                } catch (e) {
                    this.$message.error(e.message || '操作失败')
                } finally {
                    this.submitLoading = false
                }
            })
        },
        // ===== 作文类型相关 =====
        openTypeDialog(row) {
            if (row) {
                this.typeDialogTitle = '编辑类型'
                this.typeForm = {
                    id: row.id, typeKey: row.typeKey || '', typeName: row.typeName || '',
                    structureTips: row.structureTips || '', description: row.description || '',
                    sort: row.sort || 0, status: row.status != null ? row.status : 1
                }
            } else {
                this.typeDialogTitle = '新增类型'
                this.typeForm = this.initTypeForm()
            }
            this.$nextTick(() => {
                if (this.$refs.typeFormRef) this.$refs.typeFormRef.clearValidate()
            })
            this.typeDialogVisible = true
        },
        closeTypeDialog() {
            this.$refs.typeFormRef && this.$refs.typeFormRef.clearValidate()
        },
        async deleteType(row) {
            try {
                await this.$confirm(`确定删除类型"${row.typeName}"吗？该类型下题目不会被删除，但将失去类型归属。`, '删除确认', {
                    confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
                })
                await reqTypeDelete(row.id)
                this.$message.success('删除成功')
                // 若删除的是当前选中类型，回退到全部
                if (this.selectedTypeId === row.id) {
                    this.selectedTypeId = ''
                    this.query.typeId = null
                }
                await this.loadTypes()
                this.load()
            } catch (e) {
                // 后端拦截（类型下还有题目）或其它错误
                if (e && e.message) this.$message.error(e.message)
            }
        },
        // ===== 类型拖拽排序 =====
        onDragStart(e, t) {
            this.dragFromId = t.id
            e.dataTransfer.effectAllowed = 'move'
        },
        onDragOver(e, t) {
            e.dataTransfer.dropEffect = 'move'
        },
        async onDrop(e, target) {
            const fromId = this.dragFromId
            this.dragFromId = null
            if (!fromId || fromId === target.id) return
            const fromIdx = this.typeList.findIndex(x => x.id === fromId)
            const toIdx = this.typeList.findIndex(x => x.id === target.id)
            if (fromIdx < 0 || toIdx < 0) return
            // 本地先重排，提升体验
            const moving = this.typeList.splice(fromIdx, 1)[0]
            this.typeList.splice(toIdx, 0, moving)
            try {
                await reqTypeSort(this.typeList.map(x => x.id))
            } catch (err) {
                this.$message.error('排序保存失败，已恢复')
                await this.loadTypes()
            }
        },
        submitTypeForm() {
            this.$refs.typeFormRef.validate(async valid => {
                if (!valid) return
                this.typeSubmitLoading = true
                try {
                    if (this.typeForm.id) {
                        await reqTypeUpdate(this.typeForm)
                    } else {
                        await reqTypeAdd(this.typeForm)
                    }
                    this.$message.success('操作成功')
                    this.typeDialogVisible = false
                    await this.loadTypes()
                    this.load()
                } catch (e) {
                    this.$message.error(e.message || '操作失败')
                } finally {
                    this.typeSubmitLoading = false
                }
            })
        }
    }
}
</script>

<style scoped lang="scss">
.essay-question-page {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .layout {
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }

    .type-aside {
        background: #fff;
        border-right: 1px solid #ebeef5;
        padding: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;

            .type-aside-head {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 14px 15px;
                border-bottom: 1px solid #f0f0f0;
                background: #fafafa;

            .type-aside-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 15px;
                font-weight: 600;
                color: #303133;

                i { color: #409eff; font-size: 17px; }
            }
        }

        .type-list {
            flex: 1;
            overflow-y: auto;
            padding: 10px 15px;

                .type-card {
                position: relative;
                border: 1px solid #ebeef5;
                border-radius: 8px;
                padding: 10px 12px;
                margin-bottom: 12px;
                cursor: pointer;
                transition: all 0.2s;
                background: #fff;

                .type-card-top {
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    .drag-handle {
                        color: #c0c4cc;
                        cursor: grab;
                        font-size: 14px;

                        &:active { cursor: grabbing; }
                    }
                }

                .type-card-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #303133;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .type-card-bottom {
                    margin-top: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .type-card-count {
                    font-size: 12px;
                    color: #909399;
                    background: #f4f4f5;
                    border-radius: 10px;
                    padding: 1px 8px;
                }

                .type-card-tip {
                    margin-top: 6px;
                    font-size: 12px;
                    color: #a8abb2;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .type-card-actions {
                    display: none;
                    gap: 10px;

                    i {
                        font-size: 14px;
                        color: #909399;
                        cursor: pointer;

                        &:hover { color: #409eff; }
                        &.el-icon-delete:hover { color: #f56c6c; }
                    }
                }

                &:hover {
                    border-color: #b9d8ff;
                    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12);

                    .type-card-actions { display: flex; }
                }

                &.active {
                    border-color: #409eff;
                    background: #ecf5ff;

                    .type-card-count { background: #409eff; color: #fff; }
                }
            }
        }
    }

    .main-area {
        padding: 0;
        overflow-y: auto;

        .filter-card {
            margin: 10px 15px;

            ::v-deep .el-card__header {
                padding: 16px 20px;
                background: #fafafa;
                border-bottom: 1px solid #ebeef5;
            }
        }

        .table-card {
            margin: 10px 15px;

            ::v-deep .el-card__header {
                padding: 14px 20px;
                background: #fafafa;
                border-bottom: 1px solid #ebeef5;
            }
        }
    }

    .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .card-header-left {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 15px;
            font-weight: 600;
            color: #303133;

            i { font-size: 18px; color: #409eff; }

            .header-sub {
                font-size: 13px;
                font-weight: 500;
                color: #909399;
            }

            .total-badge {
                font-size: 13px;
                font-weight: 400;
                color: #909399;
                margin-left: 8px;

                b { color: #409eff; font-size: 16px; font-weight: 600; }
            }
        }

        .card-header-right {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .filter-form {
        padding: 4px 0 0;

        ::v-deep .el-form-item { margin-bottom: 8px; }
    }

    .pagination-wrapper {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    ::v-deep .el-divider--vertical {
        height: 1.2em;
        margin: 0 4px;
    }

    ::v-deep .el-table {
        th { background: #fafafa; color: #606266; font-weight: 600; font-size: 13px; }
        td { font-size: 13px; }
        .el-tag--plain { font-weight: 500; }
    }

    ::v-deep .question-dialog {
        border-radius: 6px;
        overflow: hidden;

        .el-dialog__header {
            padding: 16px 24px;
            border-bottom: 1px solid #ebeef5;

            .el-dialog__title { font-size: 16px; font-weight: 600; color: #303133; }
        }

        .el-dialog__body { padding: 24px 30px 8px; }
        .el-dialog__footer { padding: 14px 30px; border-top: 1px solid #ebeef5; }
    }
}

/* 详情弹窗内样式保持 */
.detail-scroll { max-height: 72vh; overflow-y: auto; padding: 4px; }
.detail-scroll .detail-section { margin-bottom: 16px; border-radius: 8px; }
.detail-scroll .section-title {
    font-size: 15px; font-weight: 600; color: #303133;
    padding-bottom: 12px; border-bottom: 1px solid #ebeef5; margin-bottom: 14px;
    display: flex; align-items: center; gap: 6px;
}
.info-grid .info-cell { margin-bottom: 12px; }
.info-grid .info-label { font-size: 12px; color: #909399; margin-bottom: 4px; }
.info-grid .info-value { font-size: 14px; color: #303133; font-weight: 500; }
.text-block { font-size: 14px; color: #303133; line-height: 1.8; padding: 4px 0; white-space: pre-wrap; }
.bilingual-row { min-height: 120px; }
.bilingual-header {
    display: flex; align-items: center; gap: 8px; font-size: 13px; color: #606266;
    font-weight: 500; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #f0f0f0;
}
.lang-tag {
    display: inline-flex; align-items: center; justify-content: center;
    width: 26px; height: 20px; border-radius: 4px; font-size: 11px; font-weight: 700; color: #fff;
}
.lang-tag.en { background: #409eff; }
.lang-tag.zh { background: #e6a23c; }
.bilingual-content { font-size: 14px; line-height: 1.9; white-space: pre-wrap; padding: 8px 12px; border-radius: 6px; min-height: 80px; }
.bilingual-content.en { background: #f0f7ff; color: #303133; }
.bilingual-content.zh { background: #fdf6ec; color: #303133; }

/* 弹窗渐变头部（与年级管理风格一致） */
::v-deep .essay-dialog {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
        padding: 16px 22px;
        background: linear-gradient(135deg, #2e8b74 0%, #4fae93 100%);
        border-bottom: none;

        .el-dialog__title { color: #fff; font-size: 16px; font-weight: 600; }
        .el-dialog__headerbtn .el-dialog__close { color: rgba(255,255,255,0.85); }
        .el-dialog__headerbtn .el-dialog__close:hover { color: #fff; }
    }

    .el-dialog__body { padding: 24px 30px 8px; }
    .el-dialog__footer { padding: 14px 30px; border-top: 1px solid #ebeef5; background: #fafbfc; }

    .el-button--primary {
        background-color: #2e8b74;
        border-color: #2e8b74;

        &:hover, &:focus { background-color: #3aa98a; border-color: #3aa98a; }
    }
}

.dlg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .dlg-header-left { display: flex; align-items: center; gap: 12px; }

    .dlg-header-icon {
        width: 38px; height: 38px; border-radius: 10px;
        background: rgba(255,255,255,0.22);
        display: flex; align-items: center; justify-content: center;

        i { font-size: 20px; color: #fff; }
    }

    .dlg-header-title { font-size: 16px; font-weight: 600; color: #fff; line-height: 1.4; }
    .dlg-header-desc { font-size: 12px; color: rgba(255,255,255,0.85); margin-top: 2px; }

    .dlg-header-close {
        font-size: 18px; color: rgba(255,255,255,0.85); cursor: pointer; transition: all 0.2s;

        &:hover { color: #fff; transform: rotate(90deg); }
    }
}

@media screen and (max-width: 768px) {
    .essay-question-page {
        .layout { flex-direction: column; height: auto; }
        .type-aside { width: 100% !important; border-right: none; border-bottom: 1px solid #ebeef5; }
        .type-list { max-height: 200px; }

        .card-header { flex-wrap: wrap; gap: 8px; }

        .filter-form {
            ::v-deep .el-form--inline .el-form-item { display: block; width: 100%; margin-bottom: 12px; }
            ::v-deep .el-form--inline .el-form-item .el-form-item__content { width: 100%; }
            ::v-deep .el-form--inline .el-form-item .el-form-item__content .el-select,
            ::v-deep .el-form--inline .el-form-item .el-form-item__content .el-input { width: 100% !important; }
        }

        ::v-deep .el-card__body { padding: 12px; }
        ::v-deep .el-table { overflow-x: auto; display: block; }
        ::v-deep .el-dialog { width: 95% !important; margin: 10px auto !important; }
        ::v-deep .el-dialog__body { padding: 16px; }
        ::v-deep .el-dialog__footer { padding: 12px 16px; }
        ::v-deep .el-pagination { white-space: normal; display: flex; flex-wrap: wrap; justify-content: center; gap: 4px; }
    }
}
</style>
